/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Button, Collapse, Descriptions, Row, Table } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import withStyles from 'react-jss';
import startCase from 'lodash/startCase';
import { FileDoneOutlined } from '@ant-design/icons';
import moment from 'moment';

import { getViewRPO, clearViewRPO } from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';
import styles from '../styles';
import { errorNotification, successNotification } from '../Snackbar';
import Api from '../Api';
import { get } from '../utils';
import { canAuthorizeRPO } from './rpoAuthorizers';

const ViewRPO = props => {
  const { match, classes } = props;
  const dispatch = useDispatch();

  const viewRenukaPO = useSelector(state => state.viewRenukaPO);
  const auth = useSelector(state => state.auth);
  const usedId = useMemo(() => get(auth, ['user', 'id']), null);

  useEffect(() => {
    dispatch(getViewRPO(match.params.id));
    return () => {
      dispatch(clearViewRPO());
    };
  }, []);

  const onAuthorize = useCallback(async () => {
    try {
      await Api.put(`/purchase-orders/${match.params.id}/authorize`);
      successNotification('Purchase order authorized');
      dispatch(getViewRPO(match.params.id));
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const fabricRowKey = useMemo(
    () => record => `P${record.productId}F${record.fabricId}C${record.colorId}`,
    []
  );

  const accessoryRowKey = useMemo(
    () => record => `P${record.productId}A${record.accessoryId}`,
    []
  );

  const itemRowKey = useMemo(
    () => record =>
      `N${record.name}S${record.shade}S${record.size}U${record.unitId}`,
    []
  );

  const styleRowKey = useMemo(
    () => record =>
      `P${record.productId}S${record.styleId}D${record.description}S${record.size}B${record.baseColor}`,
    []
  );
  const termRowKey = useMemo(() => record => record.id, []);

  const scroll = useMemo(() => ({ x: 2600 }), []);

  const fabricsColumns = useMemo(() => {
    const column = [
      {
        title: 'Product',
        dataIndex: 'product',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].fabrics?.[index]?.product !== value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },

      {
        title: 'Fabric',
        dataIndex: 'fabric',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].fabrics?.[index]?.fabric !== value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        title: 'Width',
        dataIndex: 'width',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].fabrics?.[index]?.width !== value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    viewRenukaPO.oldVersions[0].fabrics?.[index]?.baseColor !==
                      value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    viewRenukaPO.oldVersions[0].fabrics?.[index]?.printColor !==
                      value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
        ],
      },
      {
        title: 'RPO',
        children: [
          {
            title: 'Quantity',
            dataIndex: 'rpoQuantity',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].fabrics?.[index]?.quantity !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {+value}
              </span>
            ),
          },
          {
            title: 'Rate',
            dataIndex: 'rate',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].fabrics?.[index]?.rate !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {+value}
              </span>
            ),
          },
          {
            title: 'Value',
            dataIndex: 'value',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].fabrics?.[index]?.value !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {+value}
              </span>
            ),
          },
          {
            title: 'GST (%)',
            dataIndex: 'gst',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].fabrics?.[index]?.gst !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'GST Value',
            dataIndex: 'gstValue',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].fabrics?.[index]?.gstValue !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Final Rate',
            dataIndex: 'rpoRate',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].fabrics?.[index]
                      ?.singleTotal !== +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {+value}
              </span>
            ),
          },
          {
            title: 'Total',
            dataIndex: 'total',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].fabrics?.[index]?.total !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {+value}
              </span>
            ),
          },
        ],
      },
    ];

    if (viewRenukaPO.isJobOrder) {
      column.push({
        title: 'Outward',
        children: [
          {
            title: 'Quantity',
            dataIndex: 'totalSent',
          },
          {
            title: 'Balance',
            dataIndex: 'outwardBalance',
          },
        ],
      });
    }

    column.push(
      {
        title: 'Inward',
        children: [
          { title: 'Quantity', dataIndex: 'totalReceived' },
          { title: 'Balance', dataIndex: 'inwardBalance' },
        ],
      },
      {
        title: 'Quality Check',
        children: [
          { title: 'Accepted', dataIndex: 'acceptedQuantity' },
          { title: 'Rejected', dataIndex: 'rejectedQuantity' },
          { title: 'Altered', dataIndex: 'alteredQuantity' },
          { title: 'Balance', dataIndex: 'qcBalance' },
        ],
      }
    );

    return column;
  }, [viewRenukaPO]);

  const accessoryColumns = useMemo(() => {
    const column = [
      {
        title: 'Product',
        dataIndex: 'product',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].accessories?.[index]?.product !==
                  value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].accessories?.[index]?.accessory !==
                  value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].accessories?.[index]?.unit !== value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        title: 'RPO',
        children: [
          {
            title: 'Quantity',
            dataIndex: 'rpoQuantity',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].accessories?.[index]
                      .quantity !== +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Rate',
            dataIndex: 'rate',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].accessories?.[index]?.rate !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Value',
            dataIndex: 'value',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].accessories?.[index]?.value !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'GST (%)',
            dataIndex: 'gst',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].accessories?.[index]?.gst !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'GST Value',
            dataIndex: 'gstValue',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].accessories?.[index]
                      ?.gstValue !== +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Final Rate',
            dataIndex: 'rpoRate',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].accessories?.[index]
                      ?.singleTotal !== +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Total',
            dataIndex: 'total',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].accessories?.[index]?.total !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
        ],
      },
    ];

    if (viewRenukaPO.isJobOrder) {
      column.push({
        title: 'Outward',
        children: [
          {
            title: 'Quantity',
            dataIndex: 'totalSent',
          },
          {
            title: 'Balance',
            dataIndex: 'outwardBalance',
          },
        ],
      });
    }

    column.push(
      {
        title: 'Inward',
        children: [
          { title: 'Quantity', dataIndex: 'totalReceived' },
          { title: 'Balance', dataIndex: 'inwardBalance' },
        ],
      },
      {
        title: 'Quality Check',
        children: [
          { title: 'Accepted', dataIndex: 'acceptedQuantity' },
          { title: 'Rejected', dataIndex: 'rejectedQuantity' },
          { title: 'Altered', dataIndex: 'alteredQuantity' },
          { title: 'Balance', dataIndex: 'qcBalance' },
        ],
      }
    );

    return column;
  }, [viewRenukaPO]);

  const itemsColumns = useMemo(() => {
    const column = [
      {
        title: 'Item',
        dataIndex: 'name',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].items?.[index]?.name !== value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        title: 'Shade',
        dataIndex: 'shade',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].items?.[index]?.shade !== value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        title: 'Size',
        dataIndex: 'size',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].items?.[index]?.size !== value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].items?.[index]?.unit !== value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        title: 'RPO',
        children: [
          {
            title: 'Quantity',
            dataIndex: 'rpoQuantity',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].items?.[index]?.quantity !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Rate',
            dataIndex: 'rate',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].items?.[index]?.rate !== +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Value',
            dataIndex: 'value',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].items?.[index]?.value !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'GST (%)',
            dataIndex: 'gst',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].items?.[index]?.gst !== +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'GST Value',
            dataIndex: 'gstValue',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].items?.[index]?.gstValue !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Final Rate',
            dataIndex: 'rpoRate',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].items?.[index]?.singleTotal !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Total',
            dataIndex: 'total',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].items?.[index]?.total !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
        ],
      },
    ];

    if (viewRenukaPO.isJobOrder) {
      column.push({
        title: 'Outward',
        children: [
          {
            title: 'Quantity',
            dataIndex: 'totalSent',
          },
          {
            title: 'Balance',
            dataIndex: 'outwardBalance',
          },
        ],
      });
    }

    column.push(
      {
        title: 'Inward',
        children: [
          { title: 'Quantity', dataIndex: 'totalReceived' },
          { title: 'Balance', dataIndex: 'inwardBalance' },
        ],
      },
      {
        title: 'Quality Check',
        children: [
          { title: 'Accepted', dataIndex: 'acceptedQuantity' },
          { title: 'Rejected', dataIndex: 'rejectedQuantity' },
          { title: 'Altered', dataIndex: 'alteredQuantity' },
          { title: 'Balance', dataIndex: 'qcBalance' },
        ],
      }
    );

    return column;
  }, [viewRenukaPO]);

  const stylesColumns = useMemo(() => {
    const column = [
      {
        title: 'Product',
        dataIndex: 'product',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].styles?.[index]?.product !== value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        title: 'Style',
        dataIndex: 'style',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].styles?.[index]?.style !== value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].styles?.[index]?.description !==
                  value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        title: 'Size',
        dataIndex: 'size',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].styles?.[index]?.size !== value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].styles?.[index]?.unit !== value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    viewRenukaPO.oldVersions[0].styles?.[index]?.baseColor !==
                      value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    viewRenukaPO.oldVersions[0].styles?.[index]?.printColor !==
                      value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    viewRenukaPO.oldVersions[0].styles?.[index]
                      ?.embroideryColor !== value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    viewRenukaPO.oldVersions[0].styles?.[index]
                      ?.acidWashColor !== value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
        ],
      },
      {
        title: 'RPO',
        children: [
          {
            title: 'Quantity',
            dataIndex: 'rpoQuantity',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].styles?.[index]?.quantity !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Rate',
            dataIndex: 'rate',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].styles?.[index]?.rate !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Value',
            dataIndex: 'value',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].styles?.[index]?.value !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'GST (%)',
            dataIndex: 'gst',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].styles?.[index]?.gst !== +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'GST Value',
            dataIndex: 'gstValue',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].styles?.[index]?.gstValue !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Final Rate',
            dataIndex: 'rpoRate',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].styles?.[index]
                      ?.singleTotal !== +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
          {
            title: 'Total',
            dataIndex: 'total',
            render: (value, _record, index) => (
              <span
                style={{
                  color:
                    viewRenukaPO.oldVersions[0] !== undefined &&
                    +viewRenukaPO.oldVersions[0].styles?.[index]?.total !==
                      +value
                      ? 'Red'
                      : 'Black',
                }}
              >
                {value}
              </span>
            ),
          },
        ],
      },
    ];

    if (viewRenukaPO.isJobOrder) {
      column.push({
        title: 'Outward',
        children: [
          {
            title: 'Quantity',
            dataIndex: 'totalSent',
          },
          {
            title: 'Balance',
            dataIndex: 'outwardBalance',
          },
        ],
      });
    }

    column.push(
      {
        title: 'Inward',
        children: [
          { title: 'Quantity', dataIndex: 'totalReceived' },
          { title: 'Balance', dataIndex: 'inwardBalance' },
        ],
      },
      {
        title: 'Quality Check',
        children: [
          { title: 'Accepted', dataIndex: 'acceptedQuantity' },
          { title: 'Rejected', dataIndex: 'rejectedQuantity' },
          { title: 'Altered', dataIndex: 'alteredQuantity' },
          { title: 'Balance', dataIndex: 'qcBalance' },
        ],
      }
    );

    return column;
  }, [viewRenukaPO]);

  const termsColumns = useMemo(() => {
    return [
      {
        title: 'Term',
        dataIndex: 'term',
        render: (value, _record, index) => (
          <span
            style={{
              color:
                viewRenukaPO.oldVersions[0] !== undefined &&
                viewRenukaPO.oldVersions[0].terms?.[index]?.term !== value
                  ? 'Red'
                  : 'Black',
            }}
          >
            {value}
          </span>
        ),
      },
    ];
  }, [viewRenukaPO]);

  const fabricsVersionColumns = useMemo(() => {
    const column = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'RPO',
        children: [
          {
            title: 'Quantity',
            dataIndex: 'quantity',
          },
          {
            title: 'Rate',
            dataIndex: 'rate',
          },
          {
            title: 'Value',
            dataIndex: 'value',
          },
          {
            title: 'GST (%)',
            dataIndex: 'gst',
          },
          {
            title: 'GST Value',
            dataIndex: 'gstValue',
          },
          {
            title: 'Final Rate',
            dataIndex: 'singleTotal',
          },
          {
            title: 'Total',
            dataIndex: 'total',
          },
        ],
      },
    ];

    return column;
  }, []);

  const accessoryVersionColumns = useMemo(() => {
    const column = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'RPO',
        children: [
          {
            title: 'Quantity',
            dataIndex: 'quantity',
          },
          {
            title: 'Rate',
            dataIndex: 'rate',
          },
          {
            title: 'Value',
            dataIndex: 'value',
          },
          {
            title: 'GST (%)',
            dataIndex: 'gst',
          },
          {
            title: 'GST Value',
            dataIndex: 'gstValue',
          },
          {
            title: 'Final Rate',
            dataIndex: 'singleTotal',
          },
          {
            title: 'Total',
            dataIndex: 'total',
          },
        ],
      },
    ];

    return column;
  }, []);

  const itemsVersionColumns = useMemo(() => {
    const column = [
      {
        title: 'Item',
        dataIndex: 'name',
      },
      {
        title: 'Shade',
        dataIndex: 'shade',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'RPO',
        children: [
          {
            title: 'Quantity',
            dataIndex: 'quantity',
          },
          {
            title: 'Rate',
            dataIndex: 'rate',
          },
          {
            title: 'Value',
            dataIndex: 'value',
          },
          {
            title: 'GST (%)',
            dataIndex: 'gst',
          },
          {
            title: 'GST Value',
            dataIndex: 'gstValue',
          },
          {
            title: 'Final Rate',
            dataIndex: 'singleTotal',
          },
          {
            title: 'Total',
            dataIndex: 'total',
          },
        ],
      },
    ];

    return column;
  }, []);

  const stylesVersionColumns = useMemo(() => {
    const column = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'style',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'RPO',
        children: [
          {
            title: 'Quantity',
            dataIndex: 'quantity',
          },
          {
            title: 'Rate',
            dataIndex: 'rate',
          },
          {
            title: 'Value',
            dataIndex: 'value',
          },
          {
            title: 'GST (%)',
            dataIndex: 'gst',
          },
          {
            title: 'GST Value',
            dataIndex: 'gstValue',
          },
          {
            title: 'Final Rate',
            dataIndex: 'singleTotal',
          },
          {
            title: 'Total',
            dataIndex: 'total',
          },
        ],
      },
    ];

    return column;
  }, []);

  const termsVersionColumns = useMemo(() => {
    return [
      {
        title: 'Term',
        dataIndex: 'term',
      },
    ];
  }, []);

  const versionKey = useCallback(record => record.id, []);

  const renderOldVersion = (version, versionIndex) => {
    return (
      <Fragment key={versionIndex}>
        <Collapse>
          <Collapse.Panel
            key="1"
            header={`Version ${viewRenukaPO.oldVersions.length - versionIndex}`}
            extra={`${
              version.updatedBy
                ? `Updated By ${version.updatedBy}`
                : `Created By ${version.createdBy}`
            } at ${moment(version.updatedAt || version.createdAt).format(
              'DD MMM YYYY'
            )}`}
          >
            {version.items && version.items.length ? (
              <>
                <br />
                <Table
                  bordered
                  size="small"
                  columns={itemsVersionColumns}
                  dataSource={version.items}
                  rowKey={versionKey}
                  pagination={false}
                  scroll={scroll}
                  title={() => 'Items Table'}
                />
              </>
            ) : null}
            {version.fabrics && version.fabrics.length ? (
              <>
                <br />
                <Table
                  bordered
                  size="small"
                  columns={fabricsVersionColumns}
                  dataSource={version.fabrics}
                  rowKey={versionKey}
                  pagination={false}
                  scroll={scroll}
                  title={() => 'Fabrics Table'}
                />
              </>
            ) : null}
            {version.styles && version.styles.length ? (
              <>
                <br />
                <Table
                  bordered
                  size="small"
                  columns={stylesVersionColumns}
                  dataSource={version.styles}
                  rowKey={versionKey}
                  pagination={false}
                  scroll={scroll}
                  title={() => 'Styles Table'}
                />
              </>
            ) : null}
            {version.accessories && version.accessories.length ? (
              <>
                <br />
                <Table
                  bordered
                  size="small"
                  columns={accessoryVersionColumns}
                  dataSource={version.accessories}
                  rowKey={versionKey}
                  pagination={false}
                  scroll={scroll}
                  title={() => 'Accessories Table'}
                />
              </>
            ) : null}
            {version.terms && version.terms.length ? (
              <>
                <br />
                <Table
                  bordered
                  size="small"
                  columns={termsVersionColumns}
                  dataSource={version.terms}
                  rowKey={versionKey}
                  pagination={false}
                  scroll={scroll}
                  title={() => 'Terms Table'}
                />
              </>
            ) : null}
          </Collapse.Panel>
        </Collapse>
        <br />
      </Fragment>
    );
  };

  return (
    <>
      <Breadcrumbs
        moduleName="Purchase Order"
        moduleLink="/renukapo/list"
        subModuleName="View"
      />
      <div className={classes.tableStyle}>
        <Descriptions column={2} size="small" bordered>
          <Descriptions.Item label="RPO #">
            {viewRenukaPO.rpoNo}
          </Descriptions.Item>
          <Descriptions.Item label="Vendor">
            {viewRenukaPO.vendor}
          </Descriptions.Item>
          <Descriptions.Item label="Customer">
            {viewRenukaPO.customer}
          </Descriptions.Item>
          <Descriptions.Item label="BPO #">
            {viewRenukaPO.bpoNo}
          </Descriptions.Item>
          <Descriptions.Item label="Process">
            {startCase(viewRenukaPO.process)}
          </Descriptions.Item>
          <Descriptions.Item label="Category">
            {viewRenukaPO.isJobOrder ? 'Job order' : 'Purchase order'}
          </Descriptions.Item>
        </Descriptions>
        <br />
        <Collapse defaultActiveKey={1}>
          <Collapse.Panel
            key={1}
            header={`Version ${
              ((viewRenukaPO.oldVersions && viewRenukaPO.oldVersions.length) ||
                0) + 1
            } (Current version)`}
            extra={`${
              viewRenukaPO.updatedBy
                ? `Updated By ${viewRenukaPO.updatedBy}`
                : `Created By ${viewRenukaPO.createdBy}`
            } at ${moment(
              viewRenukaPO.updatedAt || viewRenukaPO.createdAt
            ).format('DD MMM YYYY')}`}
          >
            {viewRenukaPO.fabrics.length ? (
              <>
                <Table
                  bordered
                  size="small"
                  columns={fabricsColumns}
                  dataSource={viewRenukaPO.fabrics}
                  rowKey={fabricRowKey}
                  pagination={false}
                  scroll={scroll}
                  title={() => 'Fabrics Table'}
                />
                <br />
              </>
            ) : null}
            {viewRenukaPO.accessories.length ? (
              <>
                <Table
                  bordered
                  size="small"
                  columns={accessoryColumns}
                  dataSource={viewRenukaPO.accessories}
                  rowKey={accessoryRowKey}
                  pagination={false}
                  scroll={scroll}
                  title={() => 'Accessories Table'}
                />
                <br />
              </>
            ) : null}
            {viewRenukaPO.items.length ? (
              <>
                <Table
                  bordered
                  size="small"
                  columns={itemsColumns}
                  dataSource={viewRenukaPO.items}
                  rowKey={itemRowKey}
                  pagination={false}
                  scroll={scroll}
                  title={() => 'Items Table'}
                />
                <br />
              </>
            ) : null}
            {viewRenukaPO.styles.length ? (
              <>
                <Table
                  bordered
                  size="small"
                  columns={stylesColumns}
                  dataSource={viewRenukaPO.styles}
                  rowKey={styleRowKey}
                  pagination={false}
                  scroll={scroll}
                  title={() => 'Product Table'}
                />
              </>
            ) : null}
            {viewRenukaPO.terms && viewRenukaPO.terms.length ? (
              <>
                <br />
                <Table
                  bordered
                  size="small"
                  columns={termsColumns}
                  dataSource={viewRenukaPO.terms}
                  rowKey={termRowKey}
                  pagination={false}
                  scroll={scroll}
                  title={() => 'Terms Table'}
                />
              </>
            ) : null}
            <br />
          </Collapse.Panel>
        </Collapse>
        <br />
        {viewRenukaPO.oldVersions && viewRenukaPO.oldVersions.length
          ? viewRenukaPO.oldVersions.map(renderOldVersion)
          : null}
        <br />
        {/* HOTFIX 2026-10-29: only Prashant, Ankit and Narender can authorize an RPO (owner's decision).
            Old rule kept below for revert; to be replaced by an RBAC permission on dev.
        {!viewRenukaPO.authorizedAt &&
        (usedId === viewRenukaPO.authorizerId ||
          usedId === 2 ||
          usedId === 6 ||
          usedId === 21) ? ( */}
        {!viewRenukaPO.authorizedAt &&
        canAuthorizeRPO(usedId, viewRenukaPO.vendorId) ? (
          <Row>
            <Button
              className={classes.marginBetweenSelect}
              onClick={() => onAuthorize()}
              icon={<FileDoneOutlined />}
            >
              Authorize
            </Button>
          </Row>
        ) : null}
      </div>
    </>
  );
};

ViewRPO.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(ViewRPO);
